import React, { useState } from 'react';
import { Button } from '@medely/ui-kit';
import { useProNavigate } from '@mp/contexts/NavigationContext/NavigationContext';
import { usePoolJobCta } from '@mp/hooks/usePoolJobCta';
import { poolJobCtaLabel, type PoolJobTapAction } from '@mp/hooks/poolJobCta';
import type { PoolJobButtonJob, PoolJobButtonProps } from './interface';

const DEFAULT_TEST_ID = 'pool-job-button';

const locationOnboardingParams = (job: PoolJobButtonJob) => ({
  poolId: job.pool_id as number,
});

export const PoolJobButton = ({
  job,
  children,
  onJoin,
  onAccept,
  onNavigateInterestSubmitted,
  testId = DEFAULT_TEST_ID,
}: PoolJobButtonProps): React.ReactElement | null => {
  const { navigate } = useProNavigate();
  const { cta, action, isLoading } = usePoolJobCta(job);
  const [isActing, setIsActing] = useState(false);

  const poolId = job?.pool_id ?? null;
  const isPoolJob = poolId !== null && poolId !== undefined;

  const runSeam = async (seam?: (poolId: number) => void | Promise<unknown>) => {
    if (!seam || poolId === null) {
      return;
    }
    setIsActing(true);
    try {
      await seam(poolId);
    } finally {
      setIsActing(false);
    }
  };

  const goToOnboarding = () => navigate('LocationOnboarding', locationOnboardingParams(job));

  const handlers: Record<PoolJobTapAction, () => void | Promise<void>> = {
    none: () => undefined,
    book: () => undefined,
    navigateMissingReqs: () => navigate('MissingAndExpiringConditions'),
    navigateOnboarding: goToOnboarding,
    navigateReview: () => onNavigateInterestSubmitted?.(),
    acceptThenOnboarding: async () => {
      await runSeam(onAccept);
      goToOnboarding();
    },
    joinThenOnboarding: async () => {
      await runSeam(onJoin);
      goToOnboarding();
    },
    joinThenReview: async () => {
      await runSeam(onJoin);
      onNavigateInterestSubmitted?.();
    },
    joinThenBook: async () => {
      await runSeam(onJoin);
    },
  };

  if (isPoolJob && isLoading) {
    return (
      <Button fullWidth disabled loading testId={testId} onClick={() => undefined}>
        {poolJobCtaLabel(cta) ?? 'Unlock shift'}
      </Button>
    );
  }

  if (cta === null || action === 'book') {
    return <>{children}</>;
  }

  const busy = isLoading || isActing;

  return (
    <Button
      fullWidth
      testId={testId}
      loading={busy}
      disabled={busy}
      onClick={() => {
        void handlers[action]();
      }}
    >
      {poolJobCtaLabel(cta)}
    </Button>
  );
};

export default PoolJobButton;
